import * as React from 'react'
import * as moment from 'moment'
import Select, { components } from 'react-select'
import { OptionProps } from 'react-select/src/components/Option'
import AsyncSelect from 'react-select/async'
import { debounce } from 'lodash'

import {
  ITripFilters,
  IOrgObj,
  IProviderObject,
  ITripImportResults,
  IProviderProductObject,
  IDriverSearchObject,
  IRiderObject,
  IServiceObject,
  IRiderFilters,
  ITripSummaryFiltersDTO,
  TemporaryVehicleDTO,
  GenerateRecurringTripsRequestDTO,
  IOPtions,
  ITempVehicleCount
} from '../../../types'
import { s3Upload } from '../../../utils/aws'
import { TripStatuses } from 'src/constants/trip-statuses'
import UIMessages from 'src/utils/ui-messages'
import FilterIcon from 'src/components/icons/ui/Filter'
import { dateRangeTripsManagement, updateEndDate, updateStartDate } from 'src/containers/trips/TripFilterControls'
import { Datepicker } from '@mobiscroll/react'
import ToolTip from 'src/components/ui/ToolTip'

const statusOptions = [
  { label: 'All', value: 'all' },
  { label: 'All Active', value: 'active' },
  { label: 'All Scheduled', value: 'scheduled' },
  { label: 'All Completed Trips', value: 'completedTrips' },
  { label: 'All Non Completed Trips', value: 'nonCompletedTrips' },
  { label: 'Schedule requested', value: TripStatuses.SCHEDULE_REQUESTED },
  { label: 'Scheduled', value: TripStatuses.SCHEDULED },
  { label: 'Flex Return/Will Call', value: TripStatuses.WILL_CALL },
  { label: 'Expired', value: TripStatuses.EXPIRED },
  { label: 'Requested', value: TripStatuses.REQUESTED },
  { label: 'Waiting', value: TripStatuses.WAITING },
  { label: 'Accepted', value: TripStatuses.ACCEPTED },
  { label: 'Arrived at pickup', value: TripStatuses.AT_PICK_UP },
  { label: 'Picked up', value: TripStatuses.PICKED_UP },
  { label: 'Arrived at dropoff', value: TripStatuses.AT_DROP_OFF },
  { label: 'Dropped off', value: TripStatuses.DROPPED_OFF },
  { label: 'Completed', value: TripStatuses.COMPLETED },
  { label: 'Completed Pending Payment', value: TripStatuses.COMPLETED_PENDING_PAYMENT },
  { label: 'Manually completed', value: TripStatuses.MANUALLY_COMPLETED },
  { label: 'Cancelled', value: TripStatuses.CANCELLED },
  { label: 'Cancelled Billable', value: TripStatuses.CANCEL_BILLABLE },
  { label: 'Driver Cancelled', value: TripStatuses.DRIVER_CANCELLED },
  { label: 'No response operator', value: TripStatuses.NO_RESPONSE_OPERATOR },
  { label: 'No show rider', value: TripStatuses.NO_SHOW_RIDER },
  { label: 'Rejected', value: TripStatuses.REJECTED },
  { label: 'Booking failed', value: TripStatuses.BOOKING_FAILED },
  { label: 'Integration failed', value: TripStatuses.INTEGRATION_FAILED }
]

const timeFieldOptions = [
  { label: 'Status Created', value: 'statusCreated' },
  { label: 'Trip Created', value: 'tripCreated' },
  { label: 'Trip Date', value: 'tripDate' },
  { label: 'Scheduled Pickup', value: 'pickup' },
  { label: 'Scheduled Dropoff', value: 'dropoff' }
]

const timeStampGenerator = (unit: any, amount?: any) => {
  const date = amount ? moment().add(amount, `${unit}`) : moment()
  const start = date.startOf(unit).toISOString()
  const end = date.endOf(unit).toISOString()
  return {
    start,
    end
  }
}

const filterByTimeOptions = [
  { label: 'Today', value: timeStampGenerator('day') },
  { label: 'Tomorrow', value: timeStampGenerator('day', 1) },
  { label: 'Yesterday', value: timeStampGenerator('day', -1) },
  { label: 'This Week', value: timeStampGenerator('week') },
  { label: 'Last Week', value: timeStampGenerator('week', -1) },
  { label: 'This Month', value: timeStampGenerator('month') },
  { label: 'Last Month', value: timeStampGenerator('month', -1) }
]

const displayTimeSelector: Array<{ label: string; value: [moment.unitOfTime.StartOf, any] }> = [
  { label: 'Today', value: ['day', undefined] },
  { label: 'Tomorrow', value: ['day', 1] },
  { label: 'Yesterday', value: ['day', -1] },
  { label: 'This Week', value: ['week', undefined] },
  { label: 'Last Week', value: ['week', -1] },
  { label: 'This Month', value: ['month', undefined] },
  { label: 'Last Month', value: ['month', -1] }
]

const chargeCollectionOptions = [
  { label: 'Successful Charge', value: 'successfulCharge' },
  { label: 'Collection Error', value: 'chargeError' }
]

interface ITripsFilterControlsProps {
  isFetching: boolean
  orgId: string
  productIds: string[]
  currentOrg: IOrgObj
  type: 'provider' | 'organization'
  filters: ITripFilters
  summaryFilters: ITripSummaryFiltersDTO
  importResults: ITripImportResults
  getTripsSummaryRequest: () => Promise<any>
  updateTripFilters: (params: ITripFilters) => Promise<any>
  updateTripSummaryFilters: (params: Partial<ITripSummaryFiltersDTO>) => Promise<any>
  importTrips: (orgId: string, awsData: { Location: string; Key: string; Bucket: string }) => Promise<any>
  products: IProviderProductObject[]
  onComponentMount: (organizationId: string, type: string) => void
  getRiders: (filters: IRiderFilters) => void
  drivers: IDriverSearchObject[]
  riders: { items: IRiderObject[]; total: number }
  services: { items: IServiceObject[]; total: number }
  temporaryVehicles: TemporaryVehicleDTO[]
  generateRecurringTrips: (params: GenerateRecurringTripsRequestDTO) => string
  getTemporaryVehicleTripsCount: (serviceId: string) => Promise<void>
  tempVehicleTripsCountFetching: boolean
  totalTrips: ITempVehicleCount[]
}

interface ITripsFilterControlsState {
  processing: boolean
  importStatus: string | null
  riderSelectAll: boolean
  driverSelectAll: boolean
  productSelectAll: boolean
  servicesSelectAll: boolean
  tempVehicleSelectAll: boolean
  customFilterTime: boolean
  startDateDisplay: string | moment.Moment
  endDateDisplay: string | moment.Moment
  selectedRiders: any
  setSelectEndTime: boolean
  filters: ITripSummaryFiltersDTO
  generateRecurringTripsError: string
  generateRecurringTripsMessage: string
  selectedServiceCount: number
}

interface ITimeFilter {
  [key: string]: string
}

class TripsFilterControls extends React.Component<ITripsFilterControlsProps, ITripsFilterControlsState> {
  public state = {
    processing: false,
    importStatus: null,
    riderSelectAll: false,
    driverSelectAll: false,
    productSelectAll: false,
    servicesSelectAll: false,
    tempVehicleSelectAll: false,
    customFilterTime: false,
    // startDateDisplay: moment()
    //   .startOf('day')
    //   .format('MM/DD/YYYY hh:mmA'),
    // endDateDisplay: moment()
    //   .endOf('day')
    //   .format('MM/DD/YYYY hh:mmA'),
    startDateDisplay: dateRangeTripsManagement[0].value,
    endDateDisplay: dateRangeTripsManagement[1].value,
    selectedRiders: [],
    setSelectEndTime: true,
    filters: this.props.summaryFilters,
    generateRecurringTripsError: '',
    generateRecurringTripsMessage: '',
    selectedServiceCount: 0
  }

  private getTempVehicleCount = debounce(serviceId => this.props.getTemporaryVehicleTripsCount(serviceId), 2000)

  public componentDidUpdate(prevProps: ITripsFilterControlsProps) {
    if (prevProps.summaryFilters.timeFilter !== this.props.summaryFilters.timeFilter) {
      const { amount, unit }: { amount: any; unit: moment.unitOfTime.StartOf } = this.getAmountAndUnit()
      const date = amount ? moment().add(amount, `${unit}`) : moment()
      this.setState({
        startDateDisplay: date.startOf(unit).format('MM/DD/YYYY hh:mmA'),
        endDateDisplay: date.endOf(unit).format('MM/DD/YYYY hh:mmA'),
        customFilterTime: false
      })
    }
  }

  public async componentDidMount() {
    this.loadRiderFilters()
    this.props.onComponentMount(this.props.orgId, 'active')
    // this.loadOptions = debounce(this.loadOptions, 800)
  }

  public render() {
    const { type, importResults, tempVehicleTripsCountFetching } = this.props
    const filterTimeStart = this.state.startDateDisplay
    const filterTimeEnd = this.state.endDateDisplay

    // const [enableAutoRefresh, setEnableAutoRefresh] = React.useState(false)
    return (
      <div className='tm-filter-main-container'>
        <div className='tm-form-wrapper'>
          <div className='tm-form-inputs'>
            <div className='tm-inputs'>
              <div className='tm-form-select'>
                <label className=''>Status</label>
                <Select
                  classNamePrefix='select-control'
                  options={statusOptions}
                  value={this.getSelectedStatusOption()}
                  onChange={this.changeStatus}
                  isMulti={true}
                />
              </div>

              {/* {type === 'provider' ? (
              <div className='ml1 col-2'>
                <label>Organization</label>
                <Select
                  classNamePrefix='select-control'
                  options={this.getOrgOptions()}
                  value={this.getSelectedOrgOption()}
                  onChange={this.changeOrgFilter}
                />
              </div>
              ) : null} */}
              <div className='tm-form-select'>
                <label>Filter time field</label>
                <Select
                  classNamePrefix='select-control'
                  options={timeFieldOptions}
                  value={this.getSelectedTimeField()}
                  onChange={this.changeTimeField}
                />
              </div>
              <div className='tm-form-select'>
                <label>Filter by time</label>
                <Select
                  classNamePrefix='select-control'
                  options={filterByTimeOptions}
                  value={this.getSelectedTimeFilter()}
                  onChange={this.changeTimeFilter}
                />
              </div>
              <div className='tm-form-date'>
                <label>Time start</label>
                <Datepicker
                  controls={['calendar', 'time']}
                  dateFormat='MM/DD/YYYY'
                  inputProps={{
                    className: 'tm-date-picker'
                  }}
                  timeFormat='hh:mmA'
                  value={filterTimeStart}
                  onChange={({ value }) => this.selectTime(value, 'timeStart')}
                  onClose={() => this.setState({ customFilterTime: true })}
                />
              </div>
              <div className='tm-form-date'>
                <label>Time end</label>
                <Datepicker
                  controls={['calendar', 'time']}
                  dateFormat='MM/DD/YYYY'
                  inputProps={{
                    className: 'tm-date-picker'
                  }}
                  min={filterTimeStart}
                  timeFormat='hh:mmA'
                  value={filterTimeEnd}
                  onChange={({ value }) => this.selectTime(value, 'timeEnd')}
                  onClose={() => this.setState({ customFilterTime: true })}
                />
              </div>

              {/* <div className='ml2 col-3'>
              <Dropzone
                accept='.csv'
                className='flex items-center dropFile p2 cursor--pointer'
                onDrop={this.handleDrop}
              >
                <div className='ml2'>
                  <p className='m0 font-green'>Click to Import CSV</p>
                  <p className='m0 font-light-blue-grey'>
                    {this.state.importStatus ? this.state.importStatus : 'or Drag and Drop file here'}
                  </p>
                </div>
              </Dropzone>
            </div> */}
            </div>
            {type === 'provider' ? (
              <React.Fragment>
                <div className='tm-provider-form'>
                  <div className='tm-form-select'>
                    <label>Services</label>
                    <Select
                      classNamePrefix='select-control'
                      options={this.getServiceOptions()}
                      value={this.getSelectedServiceOptions()}
                      onChange={this.changeServiceFilter}
                      isMulti={true}
                    />
                  </div>
                  <div className='tm-form-select'>
                    <label>Products</label>
                    <Select
                      classNamePrefix='select-control'
                      options={this.getProductOptions()}
                      value={this.getSelectedProductOptions()}
                      onChange={this.changeProductFilter}
                      isMulti={true}
                    />
                  </div>
                  <div className='tm-form-select'>
                    <label>Riders</label>
                    <Select
                      classNamePrefix='select-control'
                      options={this.getRiderOptions()}
                      value={this.getSelectedRiderOptions()}
                      onChange={this.changeRiderFilter}
                      onInputChange={this.riderSearch}
                      isMulti={true}
                    />
                  </div>
                  <div className='tm-form-select'>
                    <label>Drivers</label>
                    <Select
                      classNamePrefix='select-control'
                      options={this.getDriverOptions()}
                      value={this.getSelectedDriverOptions()}
                      onChange={this.changeDriverFilter}
                      isMulti={true}
                    />
                  </div>
                  {/* <div className='ml1 col-2'>
                  <label>Team Member</label>
                  <Select
                    classNamePrefix='select-control'
                    options={this.getRiderOptions()}
                    value={this.getSelectedRiderOptions()}
                    onChange={this.changeRiderFilter}
                    isMulti={true}
                  />
                </div> */}
                  {/* TODO: OPTIONS TO BE UPDATED WITH ACTUAL TEMPORARY VEHICLES LIST API */}
                  <div className='tm-form-select'>
                    <label>Temporary Vehicle</label>
                    <Select
                      classNamePrefix='select-control'
                      options={this.getTempVehicleOptions()}
                      value={this.getSelectedTempVehicleOptions()}
                      onChange={this.changeVehicleFilter}
                      isMulti={true}
                      components={{ Option: this.tempVehicleOptions }}
                      isDisabled={tempVehicleTripsCountFetching}
                    />
                  </div>
                </div>
              </React.Fragment>
            ) : null}
            {this.shouldRenderImportResults() ? (
              <div>
                <div className='mt1 flex items-center'>
                  <p className='mr1'>
                    Num rows succeeded: <span className='font-green'>{importResults.succeeded}</span>
                  </p>
                  <p className='mr1'>
                    Num rows failed: <span className='font-red'>{importResults.failed}</span>
                  </p>
                </div>
                <div className=''>{this.renderImportErrors()}</div>
              </div>
            ) : null}
          </div>
          <div className='tm-form-actions'>
            <button
              className={
                this.props.isFetching
                  ? 'btn bg-grey rounded font-white cursor--not-allowed'
                  : 'btn bg-green rounded font-white'
              }
              onClick={this.props.isFetching ? () => true : this.showTrips}
            >
              <FilterIcon className='font-white pr1 tm-filter-icon' />
              {UIMessages.trip.showTrips}
            </button>
            {/* <ToolTip message='Feature Temporarily Disabled'> */}
            {/* <button className={'btn bg-green rounded font-white '} onClick={() => console.log('Feature temporarily disabled')}>
              {UIMessages.trip.generateRecurringTrips}
            </button> */}
            {/* </ToolTip> */}
            {/* <p><span className='font-red'>{this.state.generateRecurringTripsError}</span>
            </p> */}
            <p className='f-s'>
              <span className=''>{this.state.generateRecurringTripsMessage}</span>
            </p>
          </div>
        </div>
      </div>
    )
  }

  private getAmountAndUnit = () => {
    const timeFilter = displayTimeSelector.find(item => item.label === this.props.summaryFilters.timeFilter)
    const amount: any = timeFilter && timeFilter.value[1] ? timeFilter.value[1] : undefined
    const unit: moment.unitOfTime.StartOf = timeFilter && timeFilter.value[0] ? timeFilter.value[0] : 'day'
    return { amount, unit }
  }

  private renderImportErrors = () => {
    const { importResults } = this.props
    if (!importResults.errors) {
      return null
    }

    return importResults.errors.map((e, idx) => {
      return (
        <p className='' key={'import-error-' + e.row}>
          row: {e.row}, error: <span className='font-red'>{e.error}</span>
        </p>
      )
    })
  }

  private shouldRenderImportResults = () => {
    const { importResults } = this.props
    return importResults.succeeded !== undefined
  }

  private getSelectedTimeField = () => {
    const { summaryFilters } = this.props
    if (!summaryFilters.timeField) {
      return timeFieldOptions[2]
    }
    return timeFieldOptions.filter(to => to.value === summaryFilters.timeField)
  }

  private getSelectedTimeFilter = () => {
    const { summaryFilters } = this.props

    if (!summaryFilters.timeFilter) {
      return filterByTimeOptions[0].label
    } else if (this.state.customFilterTime === true) {
      return ''
    }
    return filterByTimeOptions.filter(to => to.label === summaryFilters.timeFilter)
  }

  private changeTimeField = (option: any) => {
    if (option) {
      this.props.updateTripSummaryFilters({ timeField: option.value })
    }
  }

  private changeTimeFilter = (option: any) => {
    if (option) {
      this.props.updateTripSummaryFilters({
        timeFilter: option.label,
        timeStart: option.value.start,
        timeEnd: option.value.end
      })
    }
  }

  private getSelectedChargeCollectionFilter = () => {
    const { filters } = this.props

    return chargeCollectionOptions.filter(to => to.value === filters.chargeCollection)
  }

  // private changeChargeCollectionFilter = (option: any) => {
  //   if (option) {
  //     this.props.updateTripSummaryFilters({
  //       chargeCollection: option.value
  //     })
  //   }
  // }

  private selectTime = (date: Date, timeField: string) => {
    const payload: ITimeFilter = {}
    const value: moment.Moment = moment(date)
    const firstRunCheck = this.state.setSelectEndTime === true && timeField === 'timeEnd'

    if (moment.isMoment(value)) {
      payload[timeField] = firstRunCheck === true ? value.endOf('day').toISOString() : value.toISOString()
      this.props.updateTripSummaryFilters(payload)
    } else if (moment(value, 'MM/DD/YYYY hh:mmA').isValid()) {
      payload[timeField] = firstRunCheck
        ? moment(value, 'MM/DD/YYYY hh:mmA')
            .endOf('day')
            .toISOString()
        : moment(value, 'MM/DD/YYYY hh:mmA').toISOString()
      this.props.updateTripSummaryFilters(payload)
    }

    if (timeField === 'timeStart') {
      updateStartDate(value)
      this.setState({ startDateDisplay: value })
      if (
        moment(new Date())
          .startOf('day')
          .toISOString() === this.props.summaryFilters.timeStart
      ) {
        this.props.updateTripSummaryFilters({ timeFilter: 'Today' })
      }
    } else {
      updateEndDate(
        firstRunCheck === true
          ? moment(value)
              .endOf('day')
              .format('MM/DD/YYYY hh:mmA')
          : value
      )

      this.setState({
        endDateDisplay:
          firstRunCheck === true
            ? moment(value)
                .endOf('day')
                .format('MM/DD/YYYY hh:mmA')
            : value
      })
    }

    if (firstRunCheck === true) {
      this.setState({ setSelectEndTime: false })
    }
  }

  private getProductOptions = () => {
    return [
      { label: 'Select All', value: 'all' },
      ...this.props.products.map(p => {
        return { label: p.name, value: p.id }
      })
    ]
  }

  private getServiceOptions = () => {
    return [
      { label: 'Select All', value: 'all' },
      ...this.props.services.items.map(p => {
        return { label: p.name, value: p.id }
      })
    ]
  }

  private getRiderOptions = () => {
    return [
      { label: 'Select All', value: 'all' },
      ...this.props.riders.items.map(p => {
        return { label: `${p.firstName} ${p.lastName}`, value: p.id }
      })
    ]
  }

  //  private loadOptions = (searchable: string, callback: any) => {
  //   this.riderSearch(searchable)

  //   setTimeout(() => {
  //     callback(this.getRiderOptions(searchable))
  //   }, 2000)
  // }

  private getDriverOptions = () => {
    return [
      { label: 'Select All', value: 'all' },
      ...this.props.drivers.map(p => {
        return { label: p.name, value: p.id }
      })
    ]
  }

  private getTempVehicleTripsCount = (vehicle: string) => {
    const { totalTrips } = this.props
    const { selectedServiceCount } = this.state

    if (selectedServiceCount === 1 && totalTrips.length > 0) {
      const totalCount = totalTrips.filter((trip: ITempVehicleCount) => trip.temporary_vehicle === vehicle)[0].total
      return Number(totalCount)
    }
    return 0
  }

  private getTempVehicleOptions = () => {
    const selectAll = { label: 'Select All', value: 'all' }
    const tempVehiclesOptions = [
      selectAll,
      ...this.props.temporaryVehicles.map(p => {
        return {
          label: p.name,
          value: p.name,
          count: this.getTempVehicleTripsCount(p.name)
        }
      })
    ]
    return tempVehiclesOptions
  }

  private tempVehicleOptions = (props: OptionProps<any>) => {
    const { Option } = components
    const { count, label } = props.data

    return (
      <Option {...props}>
        <div className='flex justify-between'>
          <span>{label}</span>
          {!!count && (
            <span
              className='bg-blue border-blue font-white px1 pb1 rounded'
              style={{
                borderRadius: '7px',
                height: '0.8em',
                fontSize: '0.75em'
              }}
            >
              {count}
            </span>
          )}
        </div>
      </Option>
    )
  }

  private getSelectedDriverOptions = () => {
    const { summaryFilters } = this.props
    const options = this.getDriverOptions()

    if (!summaryFilters.drivers || !summaryFilters.drivers.length) {
      return null
    }
    return this.state.driverSelectAll
      ? options[0]
      : options.filter(
          (op: any) => summaryFilters && summaryFilters.drivers && summaryFilters.drivers.includes(op.value)
        )
  }

  private loadRiderFilters = async () => {
    const { filters } = this.props
    if (!filters.riderIdWithName || !filters.riderIdWithName.length) {
      return
    }
    this.setState({ selectedRiders: filters.riderIdWithName })
  }

  private getSelectedRiderOptions = () => {
    const { summaryFilters } = this.props
    //  const options = this.getRiderOptions()
    if (!summaryFilters.riders || !summaryFilters.riders.length) {
      return null
    }
    const selectedRiders = this.state.selectedRiders

    return this.state.riderSelectAll ? { label: 'Select All', value: 'all' } : selectedRiders
  }

  private getSelectedProductOptions = () => {
    const { summaryFilters } = this.props
    const options = this.getProductOptions()
    if (!summaryFilters.products || !summaryFilters.products.length) {
      return []
    }
    return this.state.productSelectAll
      ? options[0]
      : options.filter(
          (op: any) => summaryFilters && summaryFilters.products && summaryFilters.products.includes(op.value)
        )
  }

  private getSelectedServiceOptions = () => {
    const { summaryFilters } = this.props
    const options = this.getServiceOptions()

    if (!summaryFilters.services || !summaryFilters.services.length) {
      return []
    }
    return this.state.servicesSelectAll
      ? options[0]
      : options.filter(
          (op: any) => summaryFilters && summaryFilters.services && summaryFilters.services.includes(op.value)
        )
  }

  private getSelectedTempVehicleOptions = () => {
    const { summaryFilters } = this.props
    const options = this.getTempVehicleOptions()
    if (!summaryFilters.temporaryVehicles || !summaryFilters.temporaryVehicles.length) {
      return []
    }
    return this.state.tempVehicleSelectAll
      ? options[0]
      : options.filter(
          (op: any) =>
            summaryFilters && summaryFilters.temporaryVehicles && summaryFilters.temporaryVehicles.includes(op.value)
        )
  }

  private changeVehicleFilter = (options: any) => {
    let tempVehicles = []
    if (options) {
      const selectAll = options.find((option: any) => option.value === 'all') ? true : false
      if (selectAll) {
        tempVehicles = ['all']
        this.setState({ tempVehicleSelectAll: true })
      } else if (selectAll === false && options) {
        tempVehicles = options.map((op: any) => op.value)
        this.setState({ tempVehicleSelectAll: false })
      }
    }

    this.props.updateTripSummaryFilters({ temporaryVehicles: tempVehicles })
  }

  private changeProductFilter = (options: any) => {
    let products = []
    if (options) {
      const selectAll = options.find((option: any) => option.value === 'all') ? true : false
      if (selectAll) {
        products = ['all']
        this.setState({ productSelectAll: true })
      } else if (selectAll === false && options) {
        products = options.map((op: any) => op.value)
        this.setState({ productSelectAll: false })
      }
    }

    this.props.updateTripSummaryFilters({ products })
  }

  private changeServiceFilter = (options: IOPtions[]) => {
    let services: string[] = []
    const opts = this.getServiceOptions()
    if (options) {
      const selectAll = options.find((option: IOPtions) => option.value === 'all') ? true : false
      if (selectAll) {
        services = opts.filter((opt: IOPtions) => opt.value !== 'all').map(opt => opt.value)
        this.setState({ servicesSelectAll: true })
      } else if (selectAll === false && options) {
        services = options.map((op: IOPtions) => op.value)
        this.setState({ servicesSelectAll: false })
      }
    }

    this.props.updateTripSummaryFilters({ services })

    // Get temporary vehicle total trip count for selected single service only
    this.setState({ selectedServiceCount: services.length })
    if (services.length === 1) {
      this.getTempVehicleCount(services[0])
    }
  }

  private changeDriverFilter = (options: any) => {
    let drivers = []
    if (options) {
      const selectAll = options.find((option: any) => option.value === 'all') ? true : false
      if (selectAll) {
        drivers = ['all']
        this.setState({ driverSelectAll: true })
      } else if (selectAll === false && options) {
        drivers = options.map((op: any) => op.value)
        this.setState({ driverSelectAll: false })
      }
    }

    this.props.updateTripSummaryFilters({ drivers })
  }

  private riderSearch = (searchInput: string) => {
    if (searchInput) {
      this.props.getRiders({ skip: 0, take: 1000, orgId: this.props.orgId, sortBy: ['name-asc'], search: searchInput })
    }
  }

  private changeRiderFilter = (options: any) => {
    let riders = []
    if (options) {
      const selectAll = options.find((option: any) => option.value === 'all') ? true : false
      if (selectAll) {
        riders = ['all']
        this.setState({ riderSelectAll: true })
      } else if (selectAll === false && options) {
        riders = options.map((op: any) => op.value)
        this.setState({ riderSelectAll: false, selectedRiders: options })
        this.props.updateTripSummaryFilters({ riders: options })
      }
    }

    this.props.updateTripSummaryFilters({ riders })
  }

  private getOrgOptions = () => {
    const { currentOrg } = this.props
    if (!currentOrg) {
      return []
    }
    return [{ label: 'All orgs', value: undefined }, { label: currentOrg.name, value: currentOrg.id }]
  }

  private getSelectedOrgOption = () => {
    const { filters, currentOrg } = this.props
    if (currentOrg && filters.orgId === currentOrg.id) {
      return { label: currentOrg.name, value: currentOrg.id }
    }

    return { label: 'All orgs', value: undefined }
  }

  // private changeOrgFilter = (option: any) => {
  //   const orgFilter = option ? option.value : undefined
  //   this.props.updateTripSummaryFilters({ orgId: orgFilter })
  // }

  private getSelectedStatusOption = () => {
    const { summaryFilters } = this.props
    const options = statusOptions
    if (!summaryFilters.status || !summaryFilters.status.length) {
      return []
    }
    return options.filter(
      (op: any) => summaryFilters && summaryFilters.status && summaryFilters.status.includes(op.value)
    )
  }

  private changeStatus = (options: any) => {
    let status = []
    if (options) {
      status = options.map((op: any) => op.value)
    }

    this.props.updateTripSummaryFilters({ status })
  }

  private showTrips = (e: any) => {
    console.log('show trips using filter: ', this.props.summaryFilters)
    // trigger getting of trip summary request, so the table component will reload
    this.props.getTripsSummaryRequest()
  }

  // private generateRecurringTrips = async (e: any) => {
  //   console.log('generateRecurringTrips: ', this.props.summaryFilters.services)
  //   const startDate = this.props.summaryFilters.timeStart
  //   const endDate = this.props.summaryFilters.timeEnd

  //   const isTodayOrAfter = moment(startDate).isSameOrAfter(moment(), 'day')
  //   const isEndDateSameOrAfter = moment(endDate).isSameOrAfter(moment(startDate), 'day')

  //   if (!isTodayOrAfter) {
  //     this.setState({ generateRecurringTripsMessage: 'Start date should not be earlier than the current date' })
  //   }

  //   if (!isEndDateSameOrAfter) {
  //     this.setState({ generateRecurringTripsMessage: 'End date should not be earlier than the start date' })
  //   }

  //   if (this.props.summaryFilters.services) {
  //     const serviceId = this.props.summaryFilters.services[0]

  //     const response = await this.props.generateRecurringTrips({
  //       serviceId,
  //       startDate,
  //       endDate,
  //       timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone
  //     })

  //     this.setState({ generateRecurringTripsMessage: response })

  //     setTimeout(() => {
  //       this.setState({ generateRecurringTripsMessage: '' })
  //     }, 5000)
  //   }
  // }

  private handleDrop = async (acceptedFiles: any) => {
    this.setState({ importStatus: 'Uploading file...' })
    acceptedFiles.map((file: any) => {
      const reader = new FileReader()
      reader.onload = () => {
        const fileAsBinaryString = reader.result
      }
      reader.onabort = () => console.log('file reading was aborted')
      reader.onerror = () => console.log('file reading has failed')
      reader.onload = async () => {
        let awsData
        try {
          const fileData = reader.result
          awsData = await s3Upload(file.name, fileData, `imports/${this.props.currentOrg.id}`)
          this.setState({ importStatus: 'Importing trips...' })
        } catch (e) {
          this.setState({ importStatus: 'Error uploading file' })
        }

        if (!awsData) {
          return
        }

        try {
          await this.props.importTrips(this.props.orgId, awsData)
        } catch (e) {
          this.setState({ importStatus: 'Error importing file' })
        }

        this.setState({ importStatus: null })
      }
      reader.readAsArrayBuffer(file)
    })
  }
}

export default TripsFilterControls



// WEBPACK FOOTER //
// ./src/components/trips/management/TripFilterControls.tsx